<script setup lang="ts">
import { LocationIcon } from '@glow/shared/components/icons';
import { formatDate } from '@glow/shared/utils/format';

export type PartnerReview = {
  id: number;
  createdAt: number;

  client: {
    fullName: string | null;
    avatarUrl: string | null;
  };
  text: string;
  rating: number;
  serviceNames?: string[];
  locationName?: string;
  photoUrls: string[];
  takenFromSite?: string | null;
};

const props = defineProps<{
  review: PartnerReview;
}>();

const clientName = computed(() => props.review.client.fullName ?? 'Клиент');
const lightboxImageIndex = ref<number>();

function selectLightboxImage(index: number) {
  lightboxImageIndex.value = index;
}
function onLightboxClose() {
  lightboxImageIndex.value = undefined;
}

const shortList = computed(() => props.review.photoUrls.slice(0, 4));
const shortListMobile = computed(() => props.review.photoUrls.slice(0, 3));
</script>

<template>
  <div class="review">
    <div
      class="d-flex justify-content-between align-items-center review__header"
    >
      <div class="d-flex justify-start align-items-center gap-2">
        <AppAvatar
          :name="clientName"
          :src="props.review.client.avatarUrl ?? undefined"
        />
        <div>
          <div class="review__name">
            {{ clientName }}
          </div>
          <AppRating :model-value="props.review.rating" />
        </div>
      </div>
      <span class="review__date">
        {{ formatDate(props.review.createdAt, { representation: 'date' }) }}
      </span>
    </div>
    <div
      v-if="props.review.photoUrls.length > 0"
      class="review__gallery gallery d-sm-none"
    >
      <div
        v-for="(photo, index) in shortListMobile"
        :key="index"
        class="gallery__item"
      >
        <img :src="photo" alt="" @click="selectLightboxImage(index)" />
        <div
          v-if="
            props.review.photoUrls.length > shortListMobile.length &&
            index === shortListMobile.length - 1
          "
          class="gallery__overlay"
          @click="selectLightboxImage(index)"
        >
          <span class="gallery__count">{{ shortListMobile.length + 1 }}+</span>
        </div>
      </div>
    </div>
    <div
      v-if="props.review.photoUrls.length > 0"
      class="review__gallery gallery d-none d-sm-flex"
    >
      <div
        v-for="(photo, index) in shortList"
        :key="index"
        class="gallery__item"
      >
        <img :src="photo" alt="" @click="selectLightboxImage(index)" />
        <div
          v-if="
            props.review.photoUrls.length > shortList.length &&
            index === shortList.length - 1
          "
          class="gallery__overlay"
        >
          <span class="gallery__count">{{ shortList.length + 1 }}+</span>
        </div>
      </div>
    </div>
    <div v-if="props.review.serviceNames" class="review__service">
      {{ props.review.serviceNames.join(', ') }}
    </div>
    <div class="review__text">
      {{ props.review.text }}
    </div>
    <NuxtLink
      v-if="props.review.takenFromSite"
      class="text-primary"
      :to="props.review.takenFromSite"
    >
      Взято с сайта {{ props.review.takenFromSite }}
    </NuxtLink>
    <div v-if="props.review.locationName" class="review__location">
      <LocationIcon />
      {{ props.review.locationName }}
    </div>
    <AppLightbox
      :image-urls="props.review.photoUrls"
      :initial-image="lightboxImageIndex"
      :model-value="lightboxImageIndex !== undefined"
      @update:model-value="onLightboxClose"
    />
  </div>
</template>

<style lang="scss" scoped>
.review {
  padding: stretch-size(375, 1920, 17, 32) 0 stretch-size(375, 1920, 18, 28);
  &:first-child {
    padding: 0 0 stretch-size(375, 1920, 18, 28);
  }
  &:not(:last-child) {
    border-bottom: 1px solid #e5e5e5;
  }

  &__header {
    margin-bottom: stretch-size(375, 1920, 12, 20);
  }

  &__name {
    font-weight: 600;
    font-size: stretch-size(375, 1920, 17, 24);
    line-height: stretch-size(375, 1920, 22, 28);
  }

  &__date {
    font-size: 17px;
    line-height: 22px;
    text-align: right;
    color: var(--app-color-black-secondary);

    @include media-breakpoint-down(sm) {
      font-size: 13px;
      line-height: 16px;
    }
  }

  &__gallery {
    margin-bottom: stretch-size(375, 1920, 12, 20);
  }

  &__service {
    font-weight: 600;
    font-size: stretch-size(375, 1920, 15, 24);
    line-height: stretch-size(375, 1920, 20, 28);
  }

  &__text {
    font-weight: 400;
    font-size: 17px;
    line-height: 24px;

    @include media-breakpoint-down(sm) {
      font-size: 15px;
      line-height: 20px;
    }
  }

  &__location {
    display: flex;
    margin-top: 10px;
    font-weight: 400;
    font-size: 17px;
    line-height: 22px;
    color: var(--app-color-primary);

    svg {
      width: 24px;
      height: 24px;
    }
  }
}

.gallery {
  display: flex;
  gap: stretch-size(375, 1920, 8, 32);

  &__item {
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    width: stretch-size(375, 1920, 109, 224);
    height: stretch-size(375, 1920, 109, 224);
    background: var(--app-color-black-secondary);
    border-radius: 16px;
    overflow: hidden;
    cursor: pointer;

    svg {
      width: 4em;
      height: 4em;
      color: var(--app-color-white-secondary);
    }

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &__overlay {
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    display: flex;
    justify-content: center;
    align-items: center;
    background: rgba(20, 20, 20, 0.5);

    span {
      font-weight: 600;
      font-size: stretch-size(375, 1920, 24, 40);
      line-height: stretch-size(375, 1920, 28, 47);
      color: #fff;
    }
  }
}
</style>
